import { CommonModule, formatDate } from '@angular/common';
import { Component, computed, inject, input, LOCALE_ID, signal } from '@angular/core';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import DataTable from 'datatables.net-dt';
import { DatatableComponent } from 'src/app/core/components/datatable/datatable.component';
import { MetricCardComponent } from 'src/app/core/components/metric-card';
import { AverageComplianceResponse } from 'src/app/core/models/analytics/vss-models';
import { formatDateRangeLabel } from 'src/app/core/shared/common/date-range.util';
import { environment } from 'src/environments/environment';

/** Plain text cells: DataTables writes raw HTML, and render.text() is its own escaper. */
const text = DataTable.render.text();

@Component({
  selector: 'app-vss-compliance-card',
  standalone: true,
  imports: [CommonModule, MetricCardComponent, DialogsModule, DatatableComponent],
  templateUrl: './vss-compliance-card.component.html',
  styleUrl: './vss-compliance-card.component.scss',
})
export class VssComplianceCardComponent {
  private locale = inject(LOCALE_ID);

  averageComplianceResponse = input<AverageComplianceResponse>(new AverageComplianceResponse());

  public organizationId = input<number | null>();
  public dateRange = input<string[] | null>(null);
  public startDate = input<string | null>();
  public endDate = input<string | null>();

  public showPopup = signal<boolean>(false);

  public modalTitle = computed((): string => {
    const start = this.startDate();
    const end = this.endDate();
    const label = start && end ? formatDateRangeLabel([start, end]) : '';
    return label ? `Average Compliance Rate (${label})` : 'Average Compliance Rate';
  });

  public datatableUrl = computed((): string => {
    const id = this.organizationId();
    return id != null ? `${environment.apiUrl}/v1/vicarius/analytics/complianceratedetails(${id})` : '';
  });

  /** The same period the card was computed for. */
  public periodParams = computed((): Record<string, string> => {
    const start = this.startDate();
    const end = this.endDate();
    return start && end
      ? { current_start: encodeURIComponent(start), current_end: encodeURIComponent(end) }
      : {};
  });

  private date = (value: string) => (value ? formatDate(value, 'MMM d, y, h:mm:ss a', this.locale) : '');

  private yesNo = (value: unknown) => (value ? 'Yes' : 'No');

  public datatableColumns = [
    { data: 'endpoint_name', name: 'endpoint_name', title: 'Endpoint | Device Name', type: 'string', render: text },
    { data: 'agent_id', name: 'agent_id', title: 'Endpoint ID', type: 'string', render: text },
    { data: 'cve_uid', name: 'cve_uid', title: 'CVE', type: 'string', render: text },
    { data: 'severity', name: 'severity', title: 'Severity', type: 'string', render: text },
    { data: 'is_compliant', name: 'is_compliant', title: 'Compliant', type: 'string', render: this.yesNo },
    { data: 'is_alive', name: 'is_alive', title: 'Endpoint Alive', type: 'string', render: this.yesNo },
    { data: 'detected_at', name: 'detected_at', title: 'Detected At', type: 'string', render: this.date },
    { data: 'last_seen', name: 'last_seen', title: 'Last Seen', type: 'string', render: this.date },
  ];

  onViewData(): void {
    this.showPopup.set(true);
  }

  close(): void {
    this.showPopup.set(false);
  }
}
